import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrochureDownloadModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    city: '',
    phone: '',
    email: '',
    requirement: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRequirementChange = (value) => {
    setFormData({
      ...formData,
      requirement: value
    });
  };

  const handleDownload = () => {
    // Create links to all brochure pages
    const brochurePages = [
      '/brochure/BROUCHER -11 page_251027_220332-01.png',
      '/brochure/BROUCHER -11 page_251027_220332-02.png',
      '/brochure/BROUCHER -11 page_251027_220332-03.png',
      '/brochure/BROUCHER -11 page_251027_220332-04.png',
      '/brochure/BROUCHER -11 page_251027_220332-05.png',
      '/brochure/BROUCHER -11 page_251027_220332-06.png',
      '/brochure/BROUCHER -11 page_251027_220332-07.png',
      '/brochure/BROUCHER -11 page_251027_220332-08.png',
      '/brochure/BROUCHER -11 page_251027_220332-09.png',
      '/brochure/BROUCHER -11 page_251027_220332-10.png',
      '/brochure/BROUCHER -11 page_251027_220332-11.png'
    ];

    // Open first page in new window (user can browse through all pages)
    window.open(brochurePages[0], '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.city || !formData.phone || !formData.email || !formData.requirement) {
      toast.error('Please fill all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Store lead in database
      await axios.post(`${API}/brochure-download`, {
        full_name: formData.fullName,
        company_name: formData.companyName || null,
        city: formData.city,
        phone: formData.phone,
        email: formData.email,
        requirement: formData.requirement
      });

      // Success notification
      toast.success('Thank you! Starting download...');

      // Start download
      handleDownload();

      // Reset form and close modal
      setFormData({
        fullName: '',
        companyName: '',
        city: '',
        phone: '',
        email: '',
        requirement: ''
      });
      
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error submitting brochure download:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto relative">
        {/* Corner Logo */}
        <div className="absolute top-4 right-12 opacity-20">
          <img 
            src="/crilam-logo-green.png" 
            alt="" 
            className="h-8 w-auto"
          />
        </div>
        
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#004534]">
            Download CriLam Product Catalogue
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-2">
            Get instant access to our complete product range and specifications
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="fullName" className="text-gray-700">
              Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="fullName"
              name="fullName"
              type="text"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="companyName" className="text-gray-700">
              Company Name <span className="text-gray-400">(Optional)</span>
            </Label>
            <Input
              id="companyName"
              name="companyName"
              type="text"
              placeholder="Enter company name"
              value={formData.companyName}
              onChange={handleChange}
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city" className="text-gray-700">
                City <span className="text-red-500">*</span>
              </Label>
              <Input
                id="city"
                name="city"
                type="text"
                placeholder="Your city"
                value={formData.city}
                onChange={handleChange}
                required
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-gray-700">
                Phone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Phone number"
                value={formData.phone}
                onChange={handleChange}
                required
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-gray-700">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="mt-1.5 rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="requirement" className="text-gray-700">
              Requirement Type <span className="text-red-500">*</span>
            </Label>
            <Select value={formData.requirement} onValueChange={handleRequirementChange} required>
              <SelectTrigger className="mt-1.5 rounded-xl">
                <SelectValue placeholder="Select your requirement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Acrylic Boards">Acrylic Boards</SelectItem>
                <SelectItem value="CNC Designer Doors">CNC Designer Doors</SelectItem>
                <SelectItem value="Wall Panels">Wall Panels</SelectItem>
                <SelectItem value="Mica Doors">Mica Doors</SelectItem>
                <SelectItem value="Veneer Doors">Veneer Doors</SelectItem>
                <SelectItem value="Dealer Enquiry">Dealer Enquiry</SelectItem>
                <SelectItem value="Architect / Designer">Architect / Designer</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full bg-[#D4AF37] hover:bg-[#C19B2E] text-white font-semibold py-6 text-base"
          >
            <Download className="h-5 w-5 mr-2" />
            {isSubmitting ? 'Processing...' : 'Download Catalogue'}
          </Button>
        </form>

        <p className="text-xs text-gray-500 text-center mt-2">
          Your information will be kept confidential and used only for product assistance
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default BrochureDownloadModal;
